import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import { getVerifastApplicantLogs } from "@/lib/api/verifastService";
import { showApiError } from "@/lib/utils/notifications";
import type { VerifastIdentityLogsModalProps } from "../../types/modals";
import type { VerifastSessionGroup } from "../../types/utils";
import {
  buildVerifastSessionGroups,
  verifastSummaryStatusBadgeVariant,
} from "../../utils/verifastLogsMapper";
import { VERIFAST_STATUS_BADGE_CLASS } from "../../utils/verifastStatusBadge";
import {
  buildVerifastModalSections,
  hasVerifastModalContent,
  type VerifastChecksSnapshot,
} from "../../utils/verifastChecksMapper";
import { VerifastChecksDetailModal } from "./VerifastChecksDetailModal";
import modalStyles from "../../styles/modals.module.css";

const tid = "application-signature-payment-tab-verifast-logs";

export function VerifastIdentityLogsModal({
  isOpen,
  onClose,
  applicantId,
  initialSessions,
}: VerifastIdentityLogsModalProps) {
  const [sessions, setSessions] = useState<VerifastSessionGroup[]>([]);
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [checksSnapshot, setChecksSnapshot] = useState<VerifastChecksSnapshot | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) return;
    setSessions(initialSessions);
    setExpandedRowIndex(initialSessions.length > 0 ? 0 : null);
  }, [isOpen, initialSessions]);

  const applySessions = (groups: VerifastSessionGroup[]) => {
    setSessions(groups);
    setExpandedRowIndex(groups.length > 0 ? 0 : null);
  };

  const refresh = async () => {
    if (refreshing || !applicantId) return;
    setRefreshing(true);
    try {
      const data = await getVerifastApplicantLogs(applicantId);
      applySessions(buildVerifastSessionGroups(data));
    } catch (error: unknown) {
      showApiError(error);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleRow = (index: number) => {
    setExpandedRowIndex((prev) => (prev === index ? null : index));
  };

  const checksSections = buildVerifastModalSections(checksSnapshot);

  return (
    <>
    <OnScreenModal
      isOpen={isOpen}
      title="Verifast identity logs"
      onClose={onClose}
      maxWidth="max-w-800"
      bodyScrollOnly
      headerVariant="centeredTitle"
      headerClassName={modalStyles.modalShellHeaderWhite}
      bodyClassName="!py-4"
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-button`}
    >
      <div className="relative flex flex-col gap-3" data-testid={`${tid}-body`}>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={refreshing}
            aria-busy={refreshing}
            data-testid={`${tid}-refresh-button`}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1597ff] px-4 py-2 text-sm font-semibold leading-none text-white shadow-sm transition-colors hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 shrink-0 ${refreshing ? "animate-spin" : ""}`}
              aria-hidden
            />
            <span className="leading-none">Refresh</span>
          </button>
        </div>

        {refreshing ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/70"
            aria-live="polite"
            aria-label="Loading Verifast logs"
          >
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin text-[#1597ff]" aria-hidden />
              <span>Loading latest logs…</span>
            </div>
          </div>
        ) : null}

        {!sessions.length ? (
          <p
            className={`m-0 text-sm text-slate-500 ${refreshing ? "opacity-50" : ""}`}
            data-testid={`${tid}-empty`}
          >
            No Verifast events found.
          </p>
        ) : (
          <div
            className={`flex flex-col gap-2 ${refreshing ? "opacity-50" : ""}`}
            data-testid={`${tid}-list`}
          >
            {sessions.map((session, i) => {
              const expanded = expandedRowIndex === i;
              const badgeVariant = verifastSummaryStatusBadgeVariant(session.statusKey);
              return (
                <div
                  key={`${session.sessionId}-${i}`}
                  className="overflow-hidden rounded-md border border-slate-200 bg-slate-50/80"
                  data-testid={`${tid}-session-${i}`}
                >
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-slate-100/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1597ff]/40"
                    onClick={() => toggleRow(i)}
                    aria-expanded={expanded}
                    aria-controls={`${tid}-steps-${i}`}
                    id={`${tid}-summary-${i}`}
                    data-testid={`${tid}-summary-${i}`}
                  >
                    <span className="text-slate-500" aria-hidden>
                      {expanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-500">
                          Session
                        </span>
                        <span className="block truncate text-sm font-semibold text-slate-900">
                          {session.sessionId}
                        </span>
                      </span>
                      {session.status && session.status !== "—" ? (
                        <span className={VERIFAST_STATUS_BADGE_CLASS[badgeVariant]}>
                          {session.status}
                        </span>
                      ) : null}
                    </span>
                  </button>

                  {expanded ? (
                    <div
                      className="border-t border-slate-200 bg-white px-3 py-3"
                      role="region"
                      aria-labelledby={`${tid}-summary-${i}`}
                      id={`${tid}-steps-${i}`}
                      data-testid={`${tid}-steps-${i}`}
                    >
                      {!session.eventRows.length ? (
                        <p className="m-0 text-sm text-slate-500">
                          No steps recorded for this session.
                        </p>
                      ) : (
                        <div className="overflow-hidden rounded-md border border-slate-200">
                          <div className="overflow-hidden [scrollbar-gutter:stable]">
                            <table className="w-full table-fixed border-separate border-spacing-0 text-left text-sm">
                              <colgroup>
                                <col className="w-[16%] min-w-[108px]" />
                                <col className="w-[28%]" />
                                <col className="w-[30%]" />
                                <col className="w-[26%]" />
                              </colgroup>
                              <thead className="bg-[#2c3e50] text-white">
                                <tr>
                                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">
                                    Status
                                  </th>
                                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">
                                    Step
                                  </th>
                                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">
                                    Event
                                  </th>
                                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">
                                    Date &amp; time
                                  </th>
                                </tr>
                              </thead>
                            </table>
                          </div>
                          <div className="max-h-56 overflow-y-auto overflow-x-hidden custom-scrollbar [scrollbar-gutter:stable]">
                            <table className="w-full table-fixed border-separate border-spacing-0 text-left text-sm">
                              <colgroup>
                                <col className="w-[16%] min-w-[108px]" />
                                <col className="w-[28%]" />
                                <col className="w-[30%]" />
                                <col className="w-[26%]" />
                              </colgroup>
                              <tbody>
                                {session.eventRows.map((row, rowIndex) => (
                                  <tr
                                    key={`${row.event}-${row.dateTimeLabel}-${rowIndex}`}
                                    className="odd:bg-white even:bg-[#f8f9fb]"
                                  >
                                    <td className="break-words px-3 py-2.5 align-middle text-slate-800">
                                      <div className="flex flex-col gap-1">
                                        <span>{row.status}</span>
                                        {row.checksAvailable && row.checks ? (
                                          <button
                                            type="button"
                                            className="inline-flex w-fit items-center rounded-md text-left text-xs font-semibold text-[#1597ff] underline decoration-[#1597ff]/40 underline-offset-2 hover:text-[#0d7acc]"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              setChecksSnapshot(row.checks || null);
                                            }}
                                            data-testid={`${tid}-checks-button-${i}-${rowIndex}`}
                                          >
                                            View checks
                                          </button>
                                        ) : null}
                                      </div>
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-slate-800">
                                      {row.step}
                                    </td>
                                    <td className="break-words px-3 py-2.5 align-top text-slate-800">
                                      {row.event}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2.5 align-top text-slate-800">
                                      {row.dateTimeLabel}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </OnScreenModal>

    <VerifastChecksDetailModal
      isOpen={hasVerifastModalContent(checksSnapshot)}
      onClose={() => setChecksSnapshot(null)}
      sections={checksSections}
    />
    </>
  );
}
